import React, { useState } from "react";
import { Button, Tag, Tooltip, Typography } from "antd";
import { BsReply } from "react-icons/bs";
import { RiRobot2Line } from "react-icons/ri";

import Attachment from "./Attachment";
import MessageBubble, { BubbleMeta } from "./MessageBubble";

const { Text } = Typography;

/**
 * One website-chat message.
 *
 * The contract shape is `{id, session, sender_type, sender_name, text,
 * quick_replies, attachment, reply_to, created_at, direction}`; the inbox endpoint may also wrap it in
 * the unified envelope (`body`, `sent_at`, `meta`). `normalizeChatMessage`
 * accepts either so REST rows and websocket pushes render identically.
 */

export const normalizeChatMessage = (message) => {
  if (!message) return message;
  const meta = message.meta || {};
  const direction =
    message.direction
    || (message.sender_type === "visitor" ? "inbound" : "outbound");
  return {
    ...message,
    body: message.body ?? message.text ?? "",
    sent_at: message.sent_at || message.created_at,
    sender_type:
      message.sender_type
      || meta.sender_type
      || (direction === "inbound" ? "visitor" : "agent"),
    sender_name: message.sender_name || meta.sender_name || "",
    quick_replies: message.quick_replies || meta.quick_replies || null,
    reply_to: message.reply_to || meta.reply_to || null,
    // REST envelopes carry `attachments` with a download url; websocket pushes
    // carry the bare `attachment`, so the staff download path is built here.
    attachments:
      message.attachments?.length || !message.attachment
        ? message.attachments || []
        : [
            {
              ...message.attachment,
              url: `/chatbot/sessions/${message.session}/attachments/${message.attachment.id}/`,
            },
          ],
    // REST envelopes say `is_internal`; websocket pushes carry `internal`.
    internal: Boolean(message.internal ?? message.is_internal),
    direction,
  };
};

// Quote text for a message, as the "Replying to" bar and quote block show it.
export const replyPreview = (row) => {
  const text = String(row?.body ?? row?.text ?? "").replace(/\s+/g, " ").trim();
  if (text) return text.length > 120 ? `${text.slice(0, 120)}…` : text;
  return row?.attachments?.length || row?.attachment || row?.attachment_name
    ? "File Attachment"
    : "Message";
};

const jumpTo = (id) => {
  const el = id && document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  el.animate?.([{ opacity: 0.35 }, { opacity: 1 }], { duration: 900 });
};

const ReplyQuote = ({ reply }) => {
  const who =
    reply.sender_type === "visitor"
      ? "Visitor"
      : reply.sender_type === "bot"
        ? "Bot"
        : reply.sender_name || "Support";
  const text = replyPreview(reply);
  return (
    <div
      role="button"
      tabIndex={0}
      title="Show original message"
      onClick={() => jumpTo(reply.id)}
      onKeyDown={(event) => (event.key === "Enter" ? jumpTo(reply.id) : null)}
      style={{
        cursor: "pointer",
        borderLeft: "3px solid #8696a0",
        background: "rgba(0,0,0,0.05)",
        borderRadius: 4,
        padding: "4px 8px",
        marginBottom: 4,
        fontSize: 12,
        color: "#54656f",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 320,
      }}
    >
      <strong style={{ marginRight: 4 }}>{who}</strong>
      {text}
    </div>
  );
};

const BOT_PALETTE = {
  outboundBubble: "#eef2f7",
  inboundBubble: "#eef2f7",
};

const ChatMessage = ({ message, palette, onReply }) => {
  const row = normalizeChatMessage(message);
  const [hover, setHover] = useState(false);

  if (row.sender_type === "system") {
    return (
      <div style={{ textAlign: "center", margin: "8px 0" }}>
        <Text type="secondary" style={{ fontSize: 11.5, fontStyle: "italic" }}>
          {row.body}
        </Text>
      </div>
    );
  }

  const isBot = row.sender_type === "bot";
  const isOutbound = row.sender_type !== "visitor";

  const canReply = Boolean(onReply) && !row.internal;

  return (
    <MessageBubble
      id={row.id}
      isOutbound={isOutbound}
      onMouseEnter={canReply ? () => setHover(true) : undefined}
      onMouseLeave={canReply ? () => setHover(false) : undefined}
      aside={
        canReply ? (
          <Tooltip title="Reply">
            <Button
              type="text"
              size="small"
              shape="circle"
              aria-label="Reply to this message"
              icon={<BsReply />}
              onClick={() => onReply(row)}
              onFocus={() => setHover(true)}
              onBlur={() => setHover(false)}
              style={{ color: "#667781", opacity: hover ? 1 : 0, transition: "opacity .12s" }}
            />
          </Tooltip>
        ) : null
      }
      palette={isBot ? BOT_PALETTE : palette}
      header={
        isBot || row.internal || (isOutbound && row.sender_name) ? (
          <div style={{ fontSize: 11, color: "#667781", marginBottom: 2 }}>
            {isBot ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <RiRobot2Line /> {row.sender_name || "Bot"}
              </span>
            ) : (
              row.sender_name
            )}
            {row.internal ? (
              <Tag color="orange" style={{ fontSize: 10, marginLeft: 6 }}>
                Internal
              </Tag>
            ) : null}
          </div>
        ) : null
      }
      footer={<BubbleMeta timestamp={row.sent_at} isOutbound={false} />}
    >
      {row.reply_to ? <ReplyQuote reply={row.reply_to} /> : null}
      {row.attachments.map((attachment) => (
        <div key={attachment.id} style={{ marginBottom: row.body ? 4 : 0 }}>
          <Attachment attachment={attachment} />
        </div>
      ))}
      {row.body ? (
        <div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {row.body}
        </div>
      ) : null}
      {Array.isArray(row.quick_replies) && row.quick_replies.length ? (
        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {row.quick_replies.map((reply, index) => (
            <Tag key={index} style={{ fontSize: 11, margin: 0 }}>
              {reply?.label || String(reply)}
            </Tag>
          ))}
        </div>
      ) : null}
    </MessageBubble>
  );
};

export default ChatMessage;
